// frontend/src/pages/UserManagement.jsx
// Module 8 patch: add resend invite button for pending/expired users.
// commit: fix(m8): add resend invite button to user management
import React, { useState, useEffect, useCallback } from 'react';
import { getUsers, inviteUser, updateUserRole, removeUser } from '../api';

const ROLES = [
  { value: 'admin',              label: 'Admin' },
  { value: 'compliance_manager', label: 'Compliance Manager' },
  { value: 'board_member',       label: 'Board Member' },
];
const ROLE_LABELS = Object.fromEntries(ROLES.map(r => [r.value, r.label]));

const STATUS_STYLE = {
  active:  { background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' },
  pending: { background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' },
  expired: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
};

export default function UserManagement({ user }) {
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  // Invite form
  const [showInvite, setShowInvite] = useState(false);
  const [invEmail,   setInvEmail]   = useState('');
  const [invName,    setInvName]    = useState('');
  const [invRole,    setInvRole]    = useState('compliance_manager');
  const [invLoading, setInvLoading] = useState(false);
  const [invError,   setInvError]   = useState('');
  const [invSuccess, setInvSuccess] = useState('');

  // Resend state — keyed by user id
  const [resendLoading, setResendLoading] = useState({});
  const [resendMsg,     setResendMsg]     = useState({});

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try { setUsers(await getUsers()); }
    catch (err) { setError(err.message || 'Failed to load users.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  async function handleRoleChange(id, role) {
    try {
      await updateUserRole(id, role);
      setUsers(prev => prev.map(u => u.id === id ? { ...u, role } : u));
    } catch (err) { alert(err.message || 'Failed to update role.'); }
  }

  async function handleRemove(id, name) {
    if (!window.confirm(`Remove ${name} from your organisation? This cannot be undone.`)) return;
    try {
      await removeUser(id);
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch (err) { alert(err.message || 'Failed to remove user.'); }
  }

  async function handleInvite(e) {
    e.preventDefault();
    setInvError(''); setInvSuccess('');
    setInvLoading(true);
    try {
      await inviteUser({ email: invEmail, name: invName, role: invRole });
      setInvSuccess(`Invite sent to ${invEmail}.`);
      setInvEmail(''); setInvName(''); setInvRole('compliance_manager');
      fetchUsers();
    } catch (err) {
      setInvError(err.message || 'Failed to send invite.');
    } finally { setInvLoading(false); }
  }

  // Resend invite — re-issues token and sends email.
  // Backend: calling POST /api/invite with same email will hit the 409 unique constraint
  // because the user row already exists. We need a dedicated resend endpoint OR
  // we soft-delete and re-create. Simplest approach that works with current backend:
  // remove the pending user then re-invite. This re-creates the row fresh.
  async function handleResend(u) {
    setResendMsg(prev => ({ ...prev, [u.id]: '' }));
    setResendLoading(prev => ({ ...prev, [u.id]: true }));
    try {
      // Remove the stale pending user first
      await removeUser(u.id);
      // Re-invite with same details
      await inviteUser({ email: u.email, name: u.name, role: u.role });
      setResendMsg(prev => ({ ...prev, [u.id]: `✓ Invite resent to ${u.email}` }));
      fetchUsers();
    } catch (err) {
      setResendMsg(prev => ({ ...prev, [u.id]: err.message || 'Failed to resend invite.' }));
    } finally {
      setResendLoading(prev => ({ ...prev, [u.id]: false }));
    }
  }

  const isAdmin = user?.role === 'admin';

  return (
    <div style={{ padding: '32px 28px', maxWidth: 960 }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#132e50' }}>User Management</h1>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
            Manage who has access to your organisation's governance platform.
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setShowInvite(s => !s); setInvError(''); setInvSuccess(''); }}
            style={{ background: '#132e50', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            {showInvite ? 'Cancel' : '+ Invite user'}
          </button>
        )}
      </div>

      {/* Invite form */}
      {showInvite && isAdmin && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '20px 24px', marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#132e50', marginBottom: 16 }}>Invite a new user</div>
          {invError   && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 5, padding: '8px 12px', color: '#dc2626', fontSize: 12, marginBottom: 12 }}>{invError}</div>}
          {invSuccess && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 5, padding: '8px 12px', color: '#166534', fontSize: 12, marginBottom: 12 }}>{invSuccess}</div>}
          <form onSubmit={handleInvite} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 180px' }}>
              <label style={labelStyle}>Full name *</label>
              <input value={invName} onChange={e => setInvName(e.target.value)} required style={inputStyle} placeholder="Jane Smith" />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={labelStyle}>Work email *</label>
              <input type="email" value={invEmail} onChange={e => setInvEmail(e.target.value)} required style={inputStyle} placeholder="jane@org.com.au" />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <label style={labelStyle}>Role *</label>
              <select value={invRole} onChange={e => setInvRole(e.target.value)} style={inputStyle}>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <button
              type="submit" disabled={invLoading}
              style={{ padding: '9px 20px', background: invLoading ? '#9ca3af' : '#b8860b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: invLoading ? 'not-allowed' : 'pointer', flexShrink: 0, height: 38 }}
            >
              {invLoading ? 'Sending…' : 'Send invite'}
            </button>
          </form>
        </div>
      )}

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ color: '#9ca3af', fontSize: 13, padding: 20 }}>Loading…</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={th}>Name</th>
                <th style={th}>Email</th>
                <th style={th}>Role</th>
                <th style={th}>Status</th>
                <th style={th}>Last login</th>
                {isAdmin && <th style={{ ...th, textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const status = u.invite_status || 'active';
                const isSelf = u.id === user?.userId;
                const canResend = isAdmin && !isSelf && (status === 'pending' || status === 'expired');
                return (
                  <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={td}>
                      <span style={{ fontWeight: 600, color: '#132e50' }}>{u.name}</span>
                      {isSelf && <span style={{ marginLeft: 6, fontSize: 10, color: '#9ca3af' }}>(you)</span>}
                    </td>
                    <td style={{ ...td, color: '#6b7280' }}>{u.email}</td>
                    <td style={td}>
                      {isAdmin && !isSelf ? (
                        <select value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)} style={{ ...inputStyle, padding: '4px 8px', fontSize: 12, width: 'auto' }}>
                          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      ) : (
                        <span>{ROLE_LABELS[u.role] || u.role}</span>
                      )}
                    </td>
                    <td style={td}>
                      <span style={{ ...STATUS_STYLE[status], fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, textTransform: 'capitalize' }}>
                        {status}
                      </span>
                    </td>
                    <td style={{ ...td, color: '#9ca3af' }}>
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    {isAdmin && (
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                          {resendMsg[u.id] && (
                            <span style={{ fontSize: 11, color: resendMsg[u.id].startsWith('✓') ? '#16a34a' : '#dc2626' }}>
                              {resendMsg[u.id]}
                            </span>
                          )}
                          {canResend && (
                            <button
                              onClick={() => handleResend(u)}
                              disabled={resendLoading[u.id]}
                              title="Resend invite email"
                              style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, color: '#374151', fontSize: 11, padding: '3px 10px', cursor: resendLoading[u.id] ? 'not-allowed' : 'pointer', opacity: resendLoading[u.id] ? 0.6 : 1 }}
                            >
                              {resendLoading[u.id] ? 'Resending…' : 'Resend invite'}
                            </button>
                          )}
                          {!isSelf && (
                            <button
                              onClick={() => handleRemove(u.id, u.name)}
                              style={{ background: 'none', border: '1px solid #fecaca', borderRadius: 4, color: '#dc2626', fontSize: 11, padding: '3px 10px', cursor: 'pointer' }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 13, color: '#132e50' };
const th = { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 };
const td = { padding: '12px 14px', color: '#374151' };
